import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom'; // Импортируем useParams для получения ID из URL
import { Box, Typography, Tabs, Tab, Container, useTheme, Card, CardContent, Divider, Button, IconButton } from '@mui/material';
import { Edit as EditIcon, Business as BusinessIcon } from '@mui/icons-material';
import EditOrganizationModal from '../../components/EditOrganizationModal'; // Корректный путь к модальному окну
import { useAuth } from '../../contexts/AuthContext';
import { UserType } from '../../types/auth';
import { ManagerDTO } from '../../types/manager'; // Возможно, не потребуется, если менеджер не является основным объектом
import { OrganizationDTO } from '../../types/organization';
import { organizationService } from '../../services/organizationService';
// import { userService } from '../../../../services/userService'; // Этот сервис, вероятно, не нужен здесь
import styles from './AdminOrganizationTasksPage.module.css'; // Создадим новый CSS модуль
import AdminOrganizationTasksList from './AdminOrganizationTasksList';

import MainInfoIconActive from '../organization-manager/dashboard/desktop/img/MainInfoActive.png';
import TasksIcon from '../organization-manager/dashboard/desktop/img/MyTask.png';
import TasksIconActive from '../organization-manager/dashboard/desktop/img/MyTaskActive.png';
import DocAndFinIcon from '../organization-manager/dashboard/desktop/img/DocAndFin.png';
import DocAndFinIconActive from '../organization-manager/dashboard/desktop/img/DocAndFinActive.png';

import MainInfoIcon from '../organization-manager/dashboard/desktop/img/MainInfo.png';


interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const AdminOrganizationTasksPage: React.FC = () => {
    const { id: organizationId } = useParams<{ id: string }>(); // Получаем ID организации из URL
    const [activeTab, setActiveTab] = useState(0);
    const [organization, setOrganization] = useState<OrganizationDTO | null>(null);
    // const [manager, setManager] = useState<ManagerDTO | null>(null); // Не нужен manager здесь
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { state } = useAuth();
    const authenticatedUser = state.user;
    const theme = useTheme();
    const navigate = useNavigate(); // Добавляем useNavigate здесь

    const [isEditModalOpen, setIsEditModalOpen] = useState(false); // Состояние для управления видимостью модального окна

    const handleOpenEditModal = () => {
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
    };

    const handleSaveOrganization = async (updatedOrganization: OrganizationDTO) => {
        try {
            setLoading(true);
            setError(null);
            // Для PlatformCurator мы передаем флаг forPlatformCuratorOrAdmin = true
            const response = await organizationService.updateOrganization(updatedOrganization); // Здесь id уже внутри updatedOrganization
            setOrganization(response); // Обновляем состояние организации в дашборде
            setIsEditModalOpen(false); // Закрываем модальное окно
        } catch (err: any) {
            console.error('Failed to update organization:', err);
            setError(err.response?.data?.message || 'Не удалось обновить данные организации.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchOrganizationData = async () => {
            if (!organizationId) {
                setError("Идентификатор организации не указан.");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);
            
            try {
                // Для PlatformCurator получаем организацию по ID с флагом для админ-доступа
                const orgResponse = await organizationService.getOrganizationById(organizationId, true); // true для forPlatformCuratorOrAdmin
                setOrganization(orgResponse);
            } catch (err: any) {
                console.error('Failed to fetch organization data:', err);
                setError(err.response?.data?.message || 'Не удалось загрузить данные организации.');
            } finally {
                setLoading(false);
            }
        };

        fetchOrganizationData();
    }, [organizationId, authenticatedUser]); // Добавляем organizationId в зависимости

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    if (loading) {
        return (
            <Container maxWidth="lg" className={styles.container}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <Typography>Загрузка...</Typography>
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" className={styles.container}>
                <Box sx={{ p: 4 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            </Container>
        );
    }

    if (!organization) {
        return (
            <Container maxWidth="lg" className={styles.container}>
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                        Организация не найдена.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        Возможно, указан неверный ID или у вас нет доступа.
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" className={styles.container}>
            <Box className={styles.header}>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                    Профиль организации: {organization.name}
                </Typography>
            </Box>

            <Box sx={{ width: '100%', mt: 4 }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="organization dashboard tabs" className={styles.tabsContainer}>
                    <Tab
                        label="Основная информация"
                        icon={<img src={activeTab === 0 ? MainInfoIconActive : MainInfoIcon} alt="Основная информация" className={styles.tabIcon} />}
                        iconPosition="start"
                        {...a11yProps(0)}
                        className={`${styles.tab} ${activeTab === 0 ? styles.tabActive : ''}`}
                    />
                    <Tab
                        label="Задачи"
                        icon={<img src={activeTab === 1 ? TasksIconActive : TasksIcon} alt="Задачи" className={styles.tabIcon} />}
                        iconPosition="start"
                        {...a11yProps(1)}
                        className={`${styles.tab} ${activeTab === 1 ? styles.tabActive : ''}`}
                    />
                    <Tab
                        label="Документы и финансы"
                        icon={<img src={activeTab === 2 ? DocAndFinIconActive : DocAndFinIcon} alt="Документы и финансы" className={styles.tabIcon} />}
                        iconPosition="start"
                        {...a11yProps(2)}
                        className={`${styles.tab} ${activeTab === 2 ? styles.tabActive : ''}`}
                    />
                </Tabs>
                <TabPanel value={activeTab} index={0}>
                    <Box className={styles.infoCard}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            {organization?.icon ? (
                                <img src={organization.icon} alt="Organization Icon" className={styles.organizationIcon} />
                            ) : (
                                <BusinessIcon sx={{ fontSize: 64 }} className={styles.organizationIcon} />
                            )}
                            <Box sx={{ ml: 2 }}>
                                <Typography variant="h6" className={styles.organizationName}>{organization?.name}</Typography>
                                {organization?.legalAddress && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                        <Typography variant="body2" color="text.secondary">{organization.legalAddress}</Typography>
                                    </Box>
                                )}
                                {organization?.directorFullName && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                        <Typography variant="body2" color="text.secondary">Директор: {organization.directorFullName}</Typography>
                                    </Box>
                                )}
                                {organization?.contactPersonFullName && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                        <Typography variant="body2" color="text.secondary">Контактное лицо: {organization.contactPersonFullName}</Typography>
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ ml: 'auto' }}>
                                <IconButton aria-label="edit organization" onClick={handleOpenEditModal}> 
                                    <EditIcon />
                                </IconButton>
                            </Box>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" sx={{ mb: 2 }}>Платежные реквизиты:</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                            <Typography variant="body2" className={styles.label}>Наименование предприятия:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.name}</Typography>
                            
                            <Typography variant="body2" className={styles.label}>Фактический адрес:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.legalAddress}</Typography>

                            <Typography variant="body2" className={styles.label}>Телефон:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.phoneNumber}</Typography>

                            <Typography variant="body2" className={styles.label}>Номер расчетного счета:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.bankAccountNumber}</Typography>

                            <Typography variant="body2" className={styles.label}>Корреспондентский счет:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.correspondentAccount}</Typography>

                            <Typography variant="body2" className={styles.label}>Наименование банка:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.bankName}</Typography>

                            <Typography variant="body2" className={styles.label}>БИК:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.bic}</Typography>

                            <Typography variant="body2" className={styles.label}>ИНН / КПП:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.inn} / {organization?.kpp}</Typography>

                            <Typography variant="body2" className={styles.label}>ОГРН:</Typography>
                            <Typography variant="body2" className={styles.value}>{organization?.ogrn}</Typography>
                        </Box>
                    </Box>
                </TabPanel>
                <TabPanel value={activeTab} index={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button 
                            variant="contained" 
                            color="primary"
                            onClick={() => navigate(`/admin/organizations/${organizationId}/tasks/create-brief`)}
                        >
                            Создать задачу
                        </Button>
                    </Box>
                    <AdminOrganizationTasksList organizationId={organizationId!} />
                </TabPanel>
                <TabPanel value={activeTab} index={2}>
                    {/* Placeholder for Документы и финансы */}
                    <Typography variant="h6">Документы и финансы</Typography>
                    <Typography>Здесь будут документы и финансовая информация для организации {organization.name}.</Typography>
                </TabPanel>
            </Box>
            {organization && (
                <EditOrganizationModal
                    open={isEditModalOpen}
                    onClose={handleCloseEditModal}
                    organization={organization}
                    onSave={handleSaveOrganization}
                />
            )}
        </Container>
    );
};

export default AdminOrganizationTasksPage;
